// Service station: buy equipment, repair, reroll an equipped item.
// Callbacks (from the world module) mutate run state; this scene re-renders.

import Phaser from 'phaser';
import { SCENES } from '../config/constants';
import { COLORS } from '../config/palette';
import type { StationSceneData } from '../core/contracts';
import { getRun } from '../core/run';
import { session } from '../core/session';
import type { ItemInstance } from '../core/types';
import { createItemCard } from '../ui/ItemCard';
import { itemName } from '../ui/format';
import { MenuNav, Panel, TYPE, UI, backdrop, createButton, footerHints, setMenuCursor, sfx, txt } from '../ui/kit';

export class StationScene extends Phaser.Scene {
  private nav!: MenuNav;
  private stationData!: StationSceneData;
  private layer!: Phaser.GameObjects.Container;
  private closing = false;

  constructor() {
    super(SCENES.STATION);
  }

  create(data: StationSceneData): void {
    this.stationData = data;
    this.closing = false;
    setMenuCursor(this, true);
    backdrop(this, 0.85);
    this.render();
  }

  private render(): void {
    this.nav?.destroy();
    this.layer?.destroy();
    this.layer = this.add.container(0, 0);
    const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      this.layer.add(o);
      return o;
    };
    const run = getRun();
    if (!run) return;
    const d = this.stationData;
    const accent = COLORS.advanced;

    add(new Panel(this, 80, 60, UI.W - 160, UI.H - 160, { fill: COLORS.uiBg, fillAlpha: 0.95, accent, ticks: true, chamfer: 22, header: 96, headerAlpha: 0.1, scanlines: true }));
    add(txt(this, 132, 82, 'SERVICE STATION', { font: 'display', size: TYPE.h1, weight: 700, spacing: 8 }));
    add(txt(this, UI.W - 132, 98, `SCRAP ${run.scrap}`, { font: 'display', size: TYPE.h3, weight: 700, color: COLORS.scrap, originX: 1 }));

    this.nav = new MenuNav(this, { layout: 'spatial', onBack: () => this.close() });

    // Shop offers.
    const kitTags = run.mech.kitTags;
    d.offers.forEach((offer, i) => {
      const x = 330 + i * 440;
      const card = createItemCard(this, offer.item, { comparison: run.inventory.compare(offer.item, kitTags) });
      card.setPosition(x, 470).setScale(0.82).setAlpha(offer.sold ? 0.3 : 1);
      add(card);
      add(
        createButton(this, x, 760, {
          label: offer.sold ? 'SOLD' : 'BUY',
          width: 300,
          height: 58,
          align: 'center',
          sublabel: offer.sold ? '' : `${offer.price}`,
          sublabelColor: COLORS.scrap,
          disabled: offer.sold || run.scrap < offer.price,
          sound: 'buy',
          onClick: () => {
            d.onBuy(offer);
            this.render();
          },
        }, this.nav),
      );
    });

    // Repair + reroll.
    const p = session.ctx?.player;
    const full = !p || p.hp >= p.stats.maxHp;
    const repair = createButton(this, 330, 870, {
      label: d.repairCost > 0 ? 'Repair 35%' : 'Repair unavailable',
      width: 380,
      height: 60,
      sublabel: d.repairCost > 0 ? `${d.repairCost}` : '',
      sublabelColor: COLORS.scrap,
      disabled: d.repairCost <= 0 || full || run.scrap < d.repairCost,
      sound: 'heal',
      onClick: () => {
        d.onRepair();
        this.render();
      },
    }, this.nav);
    add(repair);

    add(txt(this, 600, 845, `REROLL  ·  ${d.rerollCost} SCRAP`, { font: 'display', size: TYPE.label, color: COLORS.uiDim, spacing: 3 }));
    const items: ItemInstance[] = run.inventory.equippedItems().filter((it) => it.kind !== 'mod').slice(0, 5);
    items.forEach((it, i) => {
      add(
        createButton(this, 700 + i * 230, 905, {
          label: itemName(it),
          width: 220,
          height: 52,
          fontSize: 16,
          disabled: run.scrap < d.rerollCost,
          sound: 'reroll',
          onClick: () => {
            d.onReroll(it);
            this.render();
          },
        }, this.nav),
      );
    });

    const close = createButton(this, UI.W - 260, UI.H - 150, { label: 'Leave', width: 280, height: 60, align: 'center', sound: 'ui_back', onClick: () => this.close() }, this.nav);
    add(close);
    add(footerHints(this, [
      { action: 'confirm', label: 'Select' },
      { action: 'back', label: 'Leave' },
    ]));
    this.nav.focus(this.nav.items[0] ?? close, { silent: true });
    this.nav.lock(150);
  }

  update(): void {
    this.nav?.update();
  }

  private close(): void {
    if (this.closing) return;
    this.closing = true;
    sfx('ui_back');
    this.stationData.onClose();
    this.scene.stop();
  }
}
